/**
 * CHROME ZONES — the ONE definition of what is site-wide chrome and what is page body.
 *
 * Until 2026-08-16 every page carried its OWN copy of the header / announcement bar / footer inline in
 * its `sections`, and the storefront skipped the global `header`/`footer` documents whenever the body
 * already had one. Consequence, by construction: the home and the product page each owned a separate
 * header, editing one never touched the other, and a seeded product page could keep a header the seller
 * had never seen (the Atlas product page still wore the blivvy snapshot's nav). Founder call: ONE header
 * for the whole store.
 *
 * The model now: chrome lives ONLY in the global `header` / `footer` documents (one per store+locale,
 * handle 'default'); page documents hold body sections only. The storefront composes header + body +
 * footer, and the builder composes the same three into ONE flat editing surface and splits them back on
 * save — so a header edit made from any page is a store-wide edit.
 *
 * Zone membership is derived from the section TYPE alone (no provenance flag to keep in sync), which is
 * also how the builder rail has always grouped its rows.
 */
export const HEADER_SECTION_TYPES = [
    'horizontal-ticker',
    'announcement-bar',
    'header',
];
export const FOOTER_SECTION_TYPES = ['footer'];
export const CHROME_SECTION_TYPES = [
    ...HEADER_SECTION_TYPES,
    ...FOOTER_SECTION_TYPES,
];
export function zoneOfType(type) {
    if (HEADER_SECTION_TYPES.includes(type))
        return 'header';
    if (FOOTER_SECTION_TYPES.includes(type))
        return 'footer';
    return 'template';
}
/** True for the two GLOBAL chrome templates — the documents that own the store-wide header / footer. */
export function isChromeTemplate(template) {
    return template === 'header' || template === 'footer';
}
/**
 * Les gabarits qui NE PORTENT PAS l'en-tête et le pied de page de la boutique (0074, 18/10/2026).
 *
 * La fenêtre de bienvenue est le premier : elle n'est pas une page, elle est peinte PAR-DESSUS une page
 * qui porte déjà le chrome. Composer l'en-tête autour d'elle dans l'éditeur montrerait une carte de
 * 440 px coincée entre une barre d'annonce et un pied de page — et surtout, la sortie de la fenêtre
 * ré-enregistrerait un chrome VIDE par-dessus celui de la boutique, puisque c'est l'état de l'éditeur
 * qui fait foi au moment d'écrire. Le chrome de la vendeuse serait effacé en ouvrant un onglet.
 */
const CHROMELESS_TEMPLATES = ['welcome-popup'];
/**
 * Ce document reçoit-il le chrome de la boutique ? Faux pour les deux documents qui SONT le chrome, et
 * faux pour un gabarit qui n'est pas une page. Tout appel qui compose, adopte ou réécrit le chrome passe
 * par ici — c'est ce qui rend la règle vraie par construction plutôt que par vigilance.
 */
export function wearsStoreChrome(template) {
    return !isChromeTemplate(template) && !CHROMELESS_TEMPLATES.includes(template);
}
/**
 * Split a flat section list into its three zones, each keeping its own relative order. Malformed entries
 * (null / typeless — a document is untrusted input on both the SSR and the editor path) are dropped.
 */
export function splitChromeSections(sections) {
    const header = [];
    const template = [];
    const footer = [];
    for (const sec of Array.isArray(sections) ? sections : []) {
        if (!sec || typeof sec !== 'object' || typeof sec.type !== 'string')
            continue;
        const zone = zoneOfType(sec.type);
        (zone === 'header' ? header : zone === 'footer' ? footer : template).push(sec);
    }
    return { header, template, footer };
}
/**
 * The page body with every chrome section removed — what a page document is allowed to persist now.
 *
 * Also the storefront's SAFETY NET: a store whose pages predate the migration (or a draft restored from
 * an old backup) still carries inline chrome, and rendering it *on top of* the global header is exactly
 * the double-header this change exists to kill. Stripping is idempotent and free on a clean document.
 */
export function stripChromeSections(document) {
    const { template } = splitChromeSections(document.sections);
    // Identity when there was nothing to strip — keeps referential equality for memoized renderers.
    if (template.length === (Array.isArray(document.sections) ? document.sections.length : 0)) {
        return document;
    }
    return { ...document, sections: template };
}
/**
 * Build a chrome document (`header` / `footer`) around a section list. Used by the builder when it
 * splits its flat editing surface back into the three documents it loaded.
 */
export function chromeDocumentOf(source, template, handle, sections) {
    return {
        schemaVersion: source.schemaVersion,
        template: template,
        handle,
        locale: source.locale,
        sections,
    };
}
/**
 * The one composition order the storefront renders and the builder edits: top chrome (ticker →
 * announcement bar → header), then the page body, then the footer. Chrome already present in the body
 * is dropped, never duplicated.
 */
export function composeChromeSections(header, body, footer) {
    const top = splitChromeSections(header);
    const mid = splitChromeSections(body);
    const bottom = splitChromeSections(footer);
    return [...top.header, ...mid.template, ...bottom.footer];
}
//# sourceMappingURL=chrome.js.map